// Core Modules

import { Component, inject, OnDestroy, OnInit, DOCUMENT } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

// External Modules
import { CookieService } from 'ngx-cookie-service';
import { Subscription } from 'rxjs';

// Services
import { AuthService } from '@core/services/auth/auth.service';
import { ConfigService } from '@core/services/config/config.service';

@Component({
  selector: 'app-auth-login',
  imports: [ReactiveFormsModule],
  templateUrl: './auth-login.component.html',
  styleUrl: './auth-login.component.scss',
})
export class AuthLoginComponent implements OnInit, OnDestroy {
  // Injectors
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly configService = inject(ConfigService);
  private readonly cookieService = inject(CookieService);
  private readonly document = inject(DOCUMENT);
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);

  // Private Members
  private readonly auth0Domain = this.configService.auth0Domain;
  private readonly auth0Tenant = this.configService.auth0Tenant;
  private readonly subscription = new Subscription();
  private legacyFederatedDomains: string[] = [];

  // Public Members
  public customUserEmail: string = '';
  public preAuthLoginContainer: FormGroup = this.formBuilder.group({
    email: ['', [Validators.required, Validators.email]],
  });
  public preAuthLoginFormSubmitted = false;

  public ngOnInit(): void {
    this.subscription.add(
      this.activatedRoute.data.subscribe((legacyDomains: Record<string, string[]>) => {
        if (!legacyDomains?.data) return;
        this.legacyFederatedDomains = legacyDomains.data;
      })
    );
  }

  public onSubmitUserEmail(): void {
    this.cookieService.deleteAll('/');
    sessionStorage.clear();
    if (this.preAuthLoginContainer.invalid) return;
    this.preAuthLoginFormSubmitted = true;
    this.customUserEmail = this.preAuthLoginContainer?.value.email;
    if (this.legacyFederatedDomains.length > 0 && this.customUserEmail.length > 0) {
      this.customUserEmail = this.customUserEmail.toLowerCase();
      const emailDomain = this.customUserEmail.split('@')[1];
      if (emailDomain?.length > 0 && this.legacyFederatedDomains.includes(emailDomain)) {
        // Init Lock widget for legacy federated domain.
        this.authService.initializeLockWidget(this.customUserEmail, `${this.auth0Tenant}.auth0.com`);
      } else {
        // Init Lock widget for custom domain.
        this.authService.initializeLockWidget(this.customUserEmail, this.auth0Domain);
      }
      const waitForEmailElementRendered = (selector: string, callback: (email: string) => void): void => {
        const poller = setInterval(() => {
          const element = this.document?.querySelector(selector) as HTMLInputElement;
          if (element?.value === this.customUserEmail) {
            clearInterval(poller);
            callback(this.customUserEmail);
          }
        }, 100);
      };
      waitForEmailElementRendered('#authLoginContainer .auth0-lock-input', (email: string) => {
        const selectedQueryFromDocument: HTMLElement = this.document.querySelector('#authLoginContainer .auth0-lock-submit') as HTMLElement;
        const result: string[] = email.split('@');
        if (result.length > 1 && this.legacyFederatedDomains.includes(result[1])) selectedQueryFromDocument.click();
      });
    } else {
      console.log("We haven't received proper legacyFederatedDomains, value of legacyFederatedDomains = ", this.legacyFederatedDomains);
      localStorage.clear();
      this.router.navigate(['/login']);
    }
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
